// Beta-invite code handling for the web registration flow.
//
// The invite code travels in the URL *fragment* (#invite=…), never the query
// string: fragments are not sent to the server, so the code never lands in
// access logs or the Referer header. It is additionally bridged + scrubbed
// before analytics boot (see public/index.html) and re-scrubbed on mount, so it
// never reaches telemetry, browser history, or debug logs. Validation is
// server-authoritative and NON-consuming — we NEVER default-allow on a failed
// or errored check.

export const INVITE_REASON_MESSAGE = {
  ok: "",
  missing: "This invite link is missing its code. Ask your host for a fresh invite link.",
  unknown: "This invite code isn’t valid. Ask your host for a fresh invite link.",
  revoked: "This invite has been revoked. Ask your host for a new one.",
  expired: "This invite link has expired. Ask your host for a new one.",
  exhausted: "This invite has already been used up. Ask your host for a new one.",
};

const VALID_REASONS = new Set(Object.keys(INVITE_REASON_MESSAGE));

export function inviteReasonMessage(reason) {
  if (reason && VALID_REASONS.has(reason)) return INVITE_REASON_MESSAGE[reason] || INVITE_REASON_MESSAGE.unknown;
  return INVITE_REASON_MESSAGE.unknown;
}

// Pull the invite code out of a fragment like "#invite=abc" or
// "#invite=abc&foo=1". Returns "" when absent/empty. Trims surrounding space.
export function extractInviteFromFragment(hash) {
  if (!hash || typeof hash !== "string") return "";
  const h = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!h) return "";
  try {
    return (new URLSearchParams(h).get("invite") || "").trim();
  } catch {
    return "";
  }
}

// Build the registrant-facing link from a service-issued raw code. The code is
// placed in the fragment so it never travels in the query string.
export function buildInviteRegisterLink(origin, code) {
  const base = String(origin || "").replace(/\/+$/, "");
  const c = (code || "").trim();
  return c ? `${base}/register#invite=${encodeURIComponent(c)}` : `${base}/register`;
}

// Read the invite code from the pre-bootstrap bridge (full page load) first,
// then from the live fragment (SPA navigation). Does not mutate anything.
export function readBridgedInviteCode(win) {
  const w = win || (typeof window !== "undefined" ? window : null);
  if (!w) return "";
  const bridged = w.__TT_INVITE_CODE__ || "";
  const fromHash = extractInviteFromFragment(w.location && w.location.hash);
  return (bridged || fromHash || "").trim();
}

// Normalize a validate response {valid, reason} into a UI decision. Defensive:
// anything other than an explicit valid:true is treated as blocked.
export function decideInvite(resp) {
  const allowed = !!(resp && resp.valid === true);
  const reason = resp && typeof resp.reason === "string" ? resp.reason : allowed ? "ok" : "unknown";
  if (allowed) return { allowed: true, reason: "ok", message: "" };
  return { allowed: false, reason, message: inviteReasonMessage(reason) };
}
